import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { ArrowLeft, RotateCcw } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/common/Button'
import { Modal } from '@/components/common/Modal'
import { VisitFormFlow } from '@/components/visit-form/VisitFormFlow'
import { PatientDetailsStep } from '@/components/visit-form/steps/PatientDetailsStep'
import { useVisitDraftStore } from '@/store/visitDraftStore'
import { usePatientStore } from '@/store/patientStore'
import { patientApi } from '@/api/patientApi'
import { visitApi } from '@/api/visitApi'
import { patientSchema } from '@/lib/validators'
import { cleanPayload, hasMeaningfulData, toIsoDate } from '@/lib/utils'

const DRAFT_KEY = '__new__'

export default function AddPatient() {
  const navigate = useNavigate()
  const draftStore = useVisitDraftStore()
  const prepend = usePatientStore((s) => s.prepend)

  const draft = draftStore.getDraft(DRAFT_KEY)
  const [stepIndex, setStepIndex] = useState(draft.stepIndex || 0)
  const [data, setData] = useState(draft.data || {})
  const [submitting, setSubmitting] = useState(false)
  const [resetOpen, setResetOpen] = useState(false)

  // Sync step + data to the persisted draft store
  useEffect(() => {
    draftStore.setStepIndex(DRAFT_KEY, stepIndex)
  }, [stepIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (key, value) => {
    setData((d) => {
      const next = { ...d, [key]: value }
      draftStore.setSectionData(DRAFT_KEY, key, value)
      return next
    })
  }

  const buildVisitPayload = () => {
    // Pull every section that has any data
    const sections = {}
    const sectionKeys = [
      'vitals',
      'history_present_illness',
      'medical_history',
      'personal_history',
      'chief_complaint',
      'dental_history',
      'extra_oral_examination',
      'intra_oral_examination',
      'investigation',
      'provisional_diagnosis',
      'treatment_plan',
      'prescription',
      'consent',
      'doctor_notes',
    ]
    sectionKeys.forEach((k) => {
      const v = data[k]
      if (hasMeaningfulData(v)) {
        const cleaned = cleanPayload(v)
        if (cleaned !== undefined) sections[k] = cleaned
      }
    })
    return sections
  }

  const handleSubmit = async () => {
    // 1. Validate patient details
    const patientRaw = data.patient_details || {}
    const parsed = patientSchema.safeParse(patientRaw)
    if (!parsed.success) {
      toast.error('Please complete patient details first')
      setStepIndex(0)
      return
    }

    setSubmitting(true)
    let createdPatient = null
    try {
      // 2. Build patient payload
      const patientPayload = cleanPayload({
        ...parsed.data,
        dob: toIsoDate(parsed.data.dob),
        registration_date: parsed.data.registration_date
          ? toIsoDate(parsed.data.registration_date)
          : null,
      })

      createdPatient = await patientApi.create(patientPayload)
      prepend(createdPatient)
      toast.success(`Patient ${createdPatient.patient_id} created`)

      // 3. Create the first visit if there's any medical/treatment data
      const visitPayload = buildVisitPayload()
      if (Object.keys(visitPayload).length > 0) {
        try {
          await visitApi.create(createdPatient.patient_id, visitPayload)
          toast.success('First visit recorded')
        } catch {
          // toast handled by interceptor
          toast.error('Patient created, but the visit could not be saved. Open the patient to retry.')
        }
      }

      // 4. Clear draft and navigate
      draftStore.clearDraft(DRAFT_KEY)
      navigate(`/patients/${createdPatient.patient_id}`)
    } catch {
      // toast handled by interceptor
    } finally {
      setSubmitting(false)
    }
  }

  const reset = () => {
    setData({})
    setStepIndex(0)
    draftStore.clearDraft(DRAFT_KEY)
    setResetOpen(false)
    toast('Draft cleared', { icon: '🧼' })
  }

  return (
    <div className="container-page py-6 sm:py-8 lg:py-10">
      <PageHeader
        eyebrow="Phase 1 → Phase 5"
        title="Register a new patient"
        description="Walk through the case-history format. The first step creates the patient; the remaining steps build their first visit record."
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
            >
              Back
            </Button>
            <Button
              variant="ghost"
              onClick={() => setResetOpen(true)}
              leftIcon={<RotateCcw className="h-4 w-4" />}
            >
              Reset draft
            </Button>
          </>
        }
      />

      <div className="mt-6">
        <VisitFormFlow
          data={data}
          onChange={handleChange}
          stepIndex={stepIndex}
          onStepChange={setStepIndex}
          onSubmit={handleSubmit}
          submitting={submitting}
          submitLabel="Save patient & visit"
          prefixStep={{
            key: 'patient_details',
            label: 'Patient Details',
            component: PatientDetailsStep,
            schema: patientSchema,
          }}
        />
      </div>

      <Modal
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        title="Clear the draft?"
        description="Every field you've filled in this form will be removed."
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setResetOpen(false)}>
              Keep
            </Button>
            <Button variant="danger" onClick={reset}>
              Clear draft
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-600">
          Don't worry, no data has been sent to the backend yet.
        </p>
      </Modal>
    </div>
  )
}
